import { LoadingWrapper } from "components/common/layouts";
import { OneOnOneNotesIcon } from "components/icons";
import {
  CONSOLIDATED_ONE_ON_ONE_PREVIEW_TAKE,
  LIMIT_RECORDS_PER_REQUEST,
} from "constants/pagination.constant";
import { useError, useHttpPaginatedQueryService, useHttpQueryService } from "hooks";
import { useCallback } from "react";
import { OneOnOneNotesService } from "services";
import { User } from "types/user";
import { useConsolidatedDrawer } from "./ConsolidatedDrawerContext";
import ConsolidatedSectionHeader from "./ConsolidatedSectionHeader";
import OneOnOneNoteCard from "./OneOnOneNoteCard";
import OneOnOneNotesSheet from "./OneOnOneNotesSheet";

type OneOnOneNotesSectionProps = {
  reviewee: User;
};

const OneOnOneNotesSection = ({ reviewee }: OneOnOneNotesSectionProps) => {
  const { id: revieweeId, fullName: revieweeName } = reviewee;
  const { isOpen: isSheetOpen, openDrawer } = useConsolidatedDrawer<string>();

  const notesPreviewRequest = useCallback(() => {
    return OneOnOneNotesService.getNotesForUser(revieweeId, {
      take: CONSOLIDATED_ONE_ON_ONE_PREVIEW_TAKE,
      skip: 0,
    });
  }, [revieweeId]);

  const {
    result: memberNotes = [],
    isLoading,
    error,
  } = useHttpQueryService({
    request: notesPreviewRequest,
    deps: [revieweeId],
  });

  const {
    items: sheetNotes,
    hasMore: hasMoreSheetNotes,
    isLoading: isLoadingSheetNotes,
    isFetchingNextPage: isFetchingNextPageNotes,
    error: sheetNotesError,
    loadMore: loadMoreNotes,
  } = useHttpPaginatedQueryService({
    fetchPage: (query) =>
      OneOnOneNotesService.getNotesForUser(revieweeId, query),
    take: LIMIT_RECORDS_PER_REQUEST,
    requestOption: { enabled: isSheetOpen },
    deps: [revieweeId],
  });

  useError(error);
  useError(sheetNotesError);

  return (
    <>
      <ConsolidatedSectionHeader
        icon={
          <div className="shrink-0 rounded-full bg-turquoise-10 p-1 text-turquoise-100">
            <OneOnOneNotesIcon />
          </div>
        }
        title="1-on-1 Notes"
        onViewMore={() => {
          const firstNote = memberNotes[0];
          if (firstNote) {
            openDrawer(firstNote.id, { scrollToList: true });
          }
        }}
      />

      <LoadingWrapper isLoading={isLoading} className="max-h-6">
        {memberNotes.length === 0 ? (
          <p className="body-14-regular">
            No 1-on-1 notes were found for {revieweeName}.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {memberNotes.map((note) => (
              <OneOnOneNoteCard
                key={note.id}
                note={note}
                onViewDetails={() => openDrawer(note.id)}
              />
            ))}
          </div>
        )}
      </LoadingWrapper>

      <OneOnOneNotesSheet
        notes={sheetNotes}
        isLoading={isLoadingSheetNotes}
        showViewMore={hasMoreSheetNotes}
        isFetchingNextPage={isFetchingNextPageNotes}
        onViewMore={loadMoreNotes}
      />
    </>
  );
};

export default OneOnOneNotesSection;
